
/* © 2017 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, January 01, 2017
 * For information or permission request, email info@aetonixsystems.com
 */

import React from "react";
import {
	Text,
	StyleSheet,
} from "react-native";

import Style from "@aetonix/styles";
import Breakpoints from "react-native-dimensions-breakpoints";
import PropTypes from "prop-types";

import { useAtaTextSize, useIdPrefix } from "@aetonix/hooks";

const sizes = Style.text;

const textColor = Style.look.text;
const contrastTextColor = Style.look.contrast.text;

const sizeNames = Object.keys(sizes).sort(function(name1, name2){
	const size1 = sizes[name1];
	const size2 = sizes[name2];
	return size1 - size2;
});

const sizeStyles = StyleSheet.create(sizeNames.reduce(addSize, {}));

function addSize(result, name) {
	const size = sizes[name];
	result[name] = {
		fontSize: size,
	};
	return result;
}

function smaller(name) {
	const position = sizeNames.indexOf(name);
	if(position < 2) return name;
	return sizeNames[position - 1];
}

function bigger(name, multiplier, max){
	if(!multiplier) return name;
	const position = sizeNames.indexOf(name) + multiplier;
	if(position > sizeNames.length - 2 || (max && sizeNames.indexOf(max) < position))
		return bigger(name, --multiplier);
	return sizeNames[position];
}

const styles = StyleSheet.create({
	normal: {
		color: textColor
	},
	secondary: {
		color: Style.colors.secondary,
	},
	tertiary: {
		color: Style.colors.tertiary,
	},
	contrast: {
		color: contrastTextColor
	},
	black: {
		color: Style.colors.black
	},
	alert: {
		color: Style.look.alert
	},
	grey: {
		color: Style.colors.grey
	},
	red: {
		color: Style.colors.red
	},
	yellow: {
		color: Style.colors.yellow
	},
	green: {
		color: Style.colors.green
	},
	light: {
		color: Style.colors.tertiaryLight
	},
	teal: {
		color: Style.colors.darkTeal
	},
	bold: {
		fontWeight: "bold"
	},
	text: {
		fontFamily: "Nunito",
	},
	spaced: {
		letterSpacing: 2,
	},
	centered: {
		textAlign: "center",
	},
	italic: {
		fontStyle: "italic"
	},
	underline: {
		textDecorationLine: "underline"
	},
	strikethrough: {
		textDecorationLine: "line-through"
	}
});

/**
 * This component takes the following props:
 *
 *	<color name> - eg. 'black' or 'alert'
 * 	<style name> - eg. 'bold' or 'inline'
 * 	style - an object containing CSS styles
 * 	size - a size expressed as a text attribute string from Style.js
 * 	onPress - a functionthat is an action to take on press
 */

StyledText.propTypes = {
	size: PropTypes.string,
	style: PropTypes.object,
	onPress: PropTypes.func
};

export default function StyledText(props){
	const ataTextSize = useAtaTextSize();
	const idPrefix = useIdPrefix();

	let color = styles.normal;
	const maxSize = props.maxSize;

	if(props.black)
		color = styles.black;
	else if(props.contrast)
		color = styles.contrast;
	else if(props.alert)
		color = styles.alert;
	else if(props.grey)
		color = styles.grey;
	else if(props.red)
		color = styles.red;
	else if(props.yellow)
		color = styles.yellow;
	else if(props.green)
		color = styles.green;
	else if(props.secondary)
		color = styles.secondary;
	else if(props.tertiary)
		color = styles.tertiary;
	else if(props.light)
		color = styles.light;
	else if(props.teal)
		color = styles.teal;
	else if(props.color)
		color = {
			color: props.color
		};

	const size = props.size;
	const longString = props.longString;

	let sizingName = sizeNames.reduce(function(found, name){
		if(found) return found;
		if(props[name]) return name;
		if(size) return size;
		return "";
	}, "") || "standard";

	if(Breakpoints.smallerThan("mobile"))
		sizingName = smaller(sizingName);
	if(Breakpoints.smallerThan("small"))
		sizingName = smaller(sizingName);
	if(ataTextSize)
		sizingName = bigger(sizingName, ataTextSize, maxSize);

	let sizing = sizeStyles[sizingName];

	// the 'size' prop can be a name OR a number.  If specified
// it defines the icon size explicitly (i.e. overides autoscale)

	if (size) {
		if (typeof size === "string") {
			sizing = sizeStyles[size];
			if(ataTextSize) sizing = sizeStyles[bigger(size, ataTextSize, maxSize)];
		}
		if (typeof size === "number") {
			sizing = {
				fontSize: size
			};
		}
		if(longString) {
			sizing = {
				fontSize: 18
			};
		}
	}

	const styleList = [styles.text, color, sizing];
	styleList.push({
		backgroundColor: "transparent"
	});

	if(props.bold)
		styleList.push(styles.bold);

	if(props.spaced)
		styleList.push(styles.spaced);

	if(props.centered)
		styleList.push(styles.centered);

	if(props.italic)
		styleList.push(styles.italic);

	if(props.underline)
		styleList.push(styles.underline);

	if(props.strikethrough)
		styleList.push(styles.strikethrough);

	const otherStyles = props.style;
	styleList.push(otherStyles);

	const additionalProps = {};
	if (props.id) additionalProps.id = idPrefix + props.id;

	if (props.numberOfLines) {
		additionalProps.numberOfLines = props.numberOfLines;
	}

	if (props.ellipsizeMode) {
		additionalProps.ellipsizeMode = props.ellipsizeMode;
	}
	return (
		<Text selectable={!!props.selectable} style={styleList} onPress={props.onPress} {...additionalProps}>{props.children}</Text>
	);
}